import { useUserLanguages } from './useUserLanguages';
import i18n from 'i18next';

/**
 * Hook to get default label translations for invoice template fields
 * Returns primary and secondary language translations for all fields
 */
export const useDefaultLabels = () => {
  const { primaryLanguage, secondaryLanguage } = useUserLanguages();

  // Map language names to i18n language codes
  const languageCodeMap: { [key: string]: string } = {
    english: 'en',
    arabic: 'ar',
    urdu: 'ur',
    french: 'fr',
  };

  // Helper to get translation for a key in a specific language
  const getTranslation = (key: string, lang: 'primary' | 'secondary'): string => {
    const languageCode = lang === 'primary'
      ? languageCodeMap[primaryLanguage] || 'en'
      : languageCodeMap[secondaryLanguage] || 'ar';

    // Get translation for specific language
    const tForLang = i18n.getFixedT(languageCode);

    // Get base key translation from the specified language file
    const translation = tForLang(key);

    // If translation exists and is different from the key, use it
    if (translation && translation !== key) {
      return translation;
    }

    // Fallback to the key itself
    return key;
  };

  // Company field labels
  const getCompanyLabel = (field: string) => {
    const fieldMap: { [key: string]: string } = {
      name: 'companyName',
      phone: 'phoneNo',
      email: 'email',
      address: 'address',
      vat: 'vatNumber',
    };
    const translationKey = fieldMap[field] || field;
    return {
      primary: getTranslation(translationKey, 'primary'),
      secondary: getTranslation(translationKey, 'secondary'),
    };
  };

  // Customer field labels
  const getCustomerLabel = (field: string) => {
    const fieldMap: { [key: string]: string } = {
      name: 'customerName',
      phone: 'phoneNo',
      email: 'email',
      address: 'address',
      vat: 'vatNumber',
    };
    const translationKey = fieldMap[field] || field;
    return {
      primary: getTranslation(translationKey, 'primary'),
      secondary: getTranslation(translationKey, 'secondary'),
    };
  };

  // Vehicle field labels
  const getVehicleLabel = (field: string) => {
    const fieldMap: { [key: string]: string } = {
      make: 'make',
      model: 'model',
      year: 'year',
      plateNumber: 'plateNumber',
      vin: 'vin',
      color: 'color',
      fuelType: 'fuelType',
      mileage: 'mileage',
    };
    const translationKey = fieldMap[field] || field;
    return {
      primary: getTranslation(translationKey, 'primary'),
      secondary: getTranslation(translationKey, 'secondary'),
    };
  };

  // Table column labels
  const getTableColumnLabel = (column: string) => {
    const fieldMap: { [key: string]: string } = {
      serialNo: 'serialNo',
      item: 'item',
      description: 'descriptions',
      quantity: 'quantity',
      unitPrice: 'unitPrice',
      discount: 'discount',
      discountedSubtotal: 'discountedSubtotal',
      tax: 'tax',
      totalWithTax: 'totalWithTax',
      amount: 'amount',
    };
    const translationKey = fieldMap[column] || column;
    return {
      primary: getTranslation(translationKey, 'primary'),
      secondary: getTranslation(translationKey, 'secondary'),
    };
  };

  // Consolidated table column labels
  const getConsolidatedTableColumnLabel = (column: string) => {
    const fieldMap: { [key: string]: string } = {
      serialNo: 'serialNo',
      vehicleName: 'vehicleName',
      vehiclePlateNo: 'vehiclePlateNo',
      deliveryNoteNo: 'deliveryNoteNo',
      subtotal: 'subtotal',
      discount: 'discount',
      discountedSubtotal: 'discountedSubtotal',
      tax: 'taxAmount',
      totalWithTax: 'totalWithTax',
      total: 'total',
    };
    const translationKey = fieldMap[column] || column;
    return {
      primary: getTranslation(translationKey, 'primary'),
      secondary: getTranslation(translationKey, 'secondary'),
    };
  };

  // Summary item labels
  // When `inline` is true, returns "Primary / Secondary" (same pattern as bilingual inline).
  function getSummaryLabel(item: string): {
    primary: string;
    secondary: string;
  };
  function getSummaryLabel(item: string, inline: true): string;
  function getSummaryLabel(
    item: string,
    inline?: boolean,
  ): { primary: string; secondary: string } | string {
    const fieldMap: { [key: string]: string } = {
      subtotal: "subtotal",
      discount: "discount",
      discountedSubtotal: "discountedSubtotal",
      tax: "taxAmount",
      total: "total",
      creditAmount: "creditAmount",
      paidAmount: "paidAmount",
      dueAmount: "dueAmount",
    };
    const translationKey = fieldMap[item] || item;
    const primary = getTranslation(translationKey, "primary");
    const secondary = getTranslation(translationKey, "secondary");
    if (inline) {
      return secondary ? `${primary} / ${secondary}` : primary;
    }
    return { primary, secondary };
  }

  // Payment label
  const getPaymentLabel = () => {
    return {
      primary: getTranslation('paymentMode', 'primary'),
      secondary: getTranslation('paymentMode', 'secondary'),
    };
  };

  // Table section labels (packages, services, items)
  const getTableSectionLabel = (section: 'packages' | 'services' | 'items') => {
    return {
      primary: getTranslation(section, 'primary'),
      secondary: getTranslation(section, 'secondary'),
    };
  };

  // Terms and conditions label
  const getTermsLabel = () => {
    return {
      primary: getTranslation('termsAndConditionsPrimary', 'primary'),
      secondary: getTranslation('termsAndConditionsSecondary', 'secondary'),
    };
  };

  // Footer labels
  const getFooterLabel = (item: 'address' | 'email' | 'phone' | 'socialLinks' | 'pageNumber') => {
    const fieldMap: { [key: string]: string } = {
      address: 'address',
      email: 'email',
      phone: 'phoneNo',
      socialLinks: 'socialLinks',
      pageNumber: 'pageNumber',
    };
    const translationKey = fieldMap[item] || item;
    return {
      primary: getTranslation(translationKey, 'primary'),
      secondary: getTranslation(translationKey, 'secondary'),
    };
  };

  return {
    getCompanyLabel,
    getCustomerLabel,
    getVehicleLabel,
    getTableColumnLabel,
    getConsolidatedTableColumnLabel,
    getSummaryLabel,
    getPaymentLabel,
    getTableSectionLabel,
    getTermsLabel,
    getFooterLabel,
  };
};

